import type { FC } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type ErrorType = "warning" | "danger" | "success";

const SHOW_TIME = 5000;
export const ErrorAlertCtx = createContext<{
  error: string | null;
  show: (error: string, type: ErrorType, time?: number) => void;
  close: () => void;
}>({
  error: null,
  show: () => {},
  close: () => {},
});

export const ErrorAlertCtxProvider: FC<
  React.PropsWithChildren<{
    error?: string;
    time?: number;
    ErrorAlert: FC<
      React.PropsWithChildren<{ type: ErrorType; message: string }>
    >;
  }>
> = ({ error, time, ErrorAlert, children }) => {
  const [message, setMessage] = useState<string | null>(null);
  const [type, setType] = useState<ErrorType>("warning");
  const showTime = useRef(time || SHOW_TIME);
  const [show, setShow] = useState(false);
  useEffect(() => {
    error && setMessage(error);
  }, [error]);
  useEffect(() => {
    showTime.current = time || SHOW_TIME;
  }, [time]);
  useEffect(() => {
    let timer: NodeJS.Timer | null = null;
    if (show) {
      timer = setTimeout(() => {
        setShow(false);
        setMessage("");
      }, showTime.current);
    }
    return () => {
      timer && clearTimeout(timer);
    };
  }, [show]);
  const showError = useCallback(
    (error: string, type: ErrorType, time = SHOW_TIME) => {
      console.error(error);
      showTime.current = time;
      setType(type);
      setMessage(error);
      setShow(true);
    },
    []
  );

  return (
    <ErrorAlertCtx.Provider
      value={{
        error: message,
        show: showError,
        close: () => setShow(false),
      }}
    >
      {show ? (
        <>
          <ErrorAlert type={type} message={message || ""} />{" "}
          <div
            className={`alert-wrapper ${
              ["danger", "warning"].includes(type)
                ? "error-alert-wrapper"
                : "success-alert-wrapper"
            }`}
          >
            {message?.split("__")?.[0] || ""}
          </div>
        </>
      ) : null}
      {children}
    </ErrorAlertCtx.Provider>
  );
};

export const useErrorAlert = () => useContext(ErrorAlertCtx);
